import type { UpdateWorkspaceInput } from 'app/generated/gql/graphql'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { SubAction } from 'app/components/form/SmartForm'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { WorkspaceJson } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { hasRole, setWorkspace as updateWorkspace, useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { useWorkspaceStore } from 'app/stores/StoreContext'
import { gqlFetchList } from 'app/lib/graphqlClient'
import type { WorkspaceFormData } from './WorkspaceSettingsComponent'

export type SubmitHandler = (data?: Partial<WorkspaceFormData>) => Promise<WorkspaceJson | undefined>

export type UseWorkspaceSettingsReturn = {
  workspace: WorkspaceJson | undefined
  canEditWorkspace: boolean
  isDirtyForm: boolean
  isLoading: boolean
  hasLocations: boolean
  isSuperUser: boolean
  formRef: React.RefObject<HTMLFormElement | null>
  subaction: SubAction
  deleteHandler: () => void
  submitHandler: (data?: Partial<WorkspaceFormData>) => Promise<void>
  handleTouchedFields: (touched: boolean) => void
}

type UpdateFn = (
  id: string,
  input: UpdateWorkspaceInput,
) => Promise<{
  data?: { updateWorkspace?: WorkspaceJson }
  error?: { message: string }
}>
type NotifyFn = (msg: string) => void
type LogFn = (message: string, ...args: unknown[]) => void

export function createSubmitHandler(
  workspaceId: string | undefined,
  update: UpdateFn,
  onSuccess: NotifyFn,
  onError: NotifyFn,
  onLogError: LogFn,
): SubmitHandler {
  return async (data?: Partial<WorkspaceFormData>) => {
    if (!workspaceId || !data) return undefined

    const input: UpdateWorkspaceInput = {}
    if (data.name !== undefined) input.name = data.name
    if (data.promptAddendum !== undefined) input.promptAddendum = data.promptAddendum

    const result = await update(workspaceId, input)

    if (result.error) {
      onLogError('Workspace update failed', result.error)
      onError('Failed to update workspace')
      return undefined
    }

    onSuccess('Workspace updated')
    return result.data?.updateWorkspace
  }
}

export function useWorkspaceSettings(): UseWorkspaceSettingsReturn {
  const [confirm] = useConfirmation()
  const user = useUser()
  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const checkPermission = useCheckPermission()

  const canEditWorkspace = staffV2Enabled ? checkPermission(`settings:workspace`) : hasRole(user!, `orgAdmin`)

  const selectedWorkspace = useWorkspace()
  const workspaceStore = useWorkspaceStore()
  const [workspace, setWorkspace] = useState<WorkspaceJson>()
  const [isLoading, setIsLoading] = useState(false)
  const [hasLocations, setHasLocations] = useState(true)
  const [isDirtyForm, setIsDirtyForm] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const deleteHandler = () => {
    confirm({
      title: `Delete Workspace`,
      description: `Are you sure you want to delete this workspace and all its Activities, Budget, Contacts, Patients, Spend and Users?\n\nBe aware this process cannot be undone.\n`,
      variant: `danger`,
    })
      .then(async () => {
        setIsLoading(true)
        await workspaceStore.remove(workspace?.id!)
        await updateWorkspace(null)
        notifySuccess(`Workspace deleted`)
      })
      .catch((err) => {
        logError(err)
        notifyError(`Workspace not deleted. ${err}`)
      })
      .finally(() => setIsLoading(false))
  }

  // AuthStore only loads workspace id/name. Settings needs the full document
  // (e.g. promptAddendum) from WorkspaceStore.
  useEffect(() => {
    const id = selectedWorkspace?.id
    if (!id) {
      setWorkspace(undefined)
      return
    }

    bg(async () => {
      try {
        const result = await workspaceStore.fetchOne(id, { requestPolicy: `network-only` })
        const full = (result.data as { workspace?: WorkspaceJson } | undefined)?.workspace
        if (full) {
          setWorkspace(full)
          return
        }
        if (result.error) logError(`Error fetching workspace`, result.error)
        setWorkspace(selectedWorkspace as WorkspaceJson)
      } catch (error) {
        logError(`Error fetching workspace`, error)
        setWorkspace(selectedWorkspace as WorkspaceJson)
      }
    })
  }, [selectedWorkspace?.id, workspaceStore])

  useEffect(() => {
    if (!workspace) return
    bg(async () => {
      try {
        const result = await gqlFetchList('locations', { search: 'workspaceId:' + workspace?.id }, 'id name')
        if (!result.hits) {
          logError(`Error fetching workspace locations`)
          return
        }
        setHasLocations((result.hits.length ?? 0) > 0)
      } catch (error) {
        logError(`Error fetching workspace locations`, error)
      }
    })
  }, [workspace])

  const handleTouchedFields = useCallback((touched: boolean) => {
    setIsDirtyForm(touched)
  }, [])

  const submitHandler = useCallback(
    async (data?: Partial<WorkspaceFormData>) => {
      const update: UpdateFn = async (id, input) => {
        const result = await workspaceStore.update(id, input)
        return {
          data: result.data as { updateWorkspace?: WorkspaceJson } | undefined,
          error: result.error ? { message: result.error.message } : undefined,
        }
      }
      const handler = createSubmitHandler(workspace?.id, update, notifySuccess, notifyError, logError)
      const updated = await handler(data)
      if (updated) setWorkspace(updated)
      setIsDirtyForm(false)
    },
    [workspace?.id, workspaceStore],
  )

  return {
    workspace,
    canEditWorkspace,
    isDirtyForm,
    isLoading,
    hasLocations,
    isSuperUser: user?.type === `super`,
    formRef,
    subaction: workspace?.id ? SubAction.EDIT : SubAction.CREATE,
    deleteHandler,
    submitHandler,
    handleTouchedFields,
  }
}
